import { getApiBaseUrl } from '@/services/Http';

/**
 * Service for handling attachment/file operations
 * Provides environment-aware URL generation for file downloads and uploads
 */
class AttachmentService {
    /**
     * Get the download URL for an attachment
     * @param attachmentId - The GUID of the attachment
     * @returns Full URL to download the file
     */
    public getDownloadUrl(attachmentId: string): string {
        const baseUrl = getApiBaseUrl();
        return `${baseUrl}/Data/Download/${attachmentId}`;
    }

    /**
     * Get the upload endpoint URL
     * @returns Full URL for file upload endpoint
     */
    public getUploadUrl(): string {
        const baseUrl = getApiBaseUrl();
        return `${baseUrl}/Data/Upload`;
    }

    /**
     * Get the delete endpoint URL
     * @param attachmentId - The GUID of the attachment to delete
     * @returns Full URL for delete endpoint
     */
    public getDeleteUrl(attachmentId: string): string {
        const baseUrl = getApiBaseUrl();
        return `${baseUrl}/Data/Delete/${attachmentId}`;
    }

    /**
     * Get the list endpoint URL
     * @returns Full URL for list endpoint
     */
    public getListUrl(): string {
        const baseUrl = getApiBaseUrl();
        return `${baseUrl}/Data/List`;
    }

    /**
     * Open a file in a new browser tab
     * @param attachmentId - The GUID of the attachment
     */
    public openInNewTab(attachmentId: string): void {
        const url = this.getDownloadUrl(attachmentId);
        window.open(url, '_blank');
    }

    /**
     * Check if an attachment is a finalized PDF
     * @param attachment - The attachment object
     * @returns true if the attachment has Extra='finalized'
     */
    public isFinalizedPDF(attachment: any): boolean {
        return attachment?.Extra === 'finalized';
    }

    /**
     * Find the finalized PDF in a list of attachments
     * @param attachments - Array of attachment objects
     * @returns The finalized PDF attachment or null if not found
     */
    public findFinalizedPDF(attachments: any[]): any | null {
        return attachments.find(f => this.isFinalizedPDF(f)) || null;
    }

    /**
     * Check if a file is web-viewable based on its extension
     * @param filename - The filename to check
     * @returns true if the file can be viewed in browser
     */
    public isWebViewable(filename: string): boolean {
        if (!filename) return false;
        const ext = filename.toLowerCase().split('.').pop() || '';
        const viewableExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'pdf', 'txt', 'json', 'xml', 'html', 'htm'];
        return viewableExtensions.includes(ext);
    }

    /**
     * Download a file to disk using authenticated request
     * @param attachmentId - The GUID of the attachment
     * @param filename - Optional filename for the download
     */
    public async downloadFile(attachmentId: string, filename?: string): Promise<void> {
        const url = this.getDownloadUrl(attachmentId);
        
        // Fetch with authentication (Http service handles Bearer token)
        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${await this.getBearerToken()}`
            }
        });
        
        if (!response.ok) {
            throw new Error(`Failed to download file: ${response.statusText}`);
        }
        
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Create temporary link for download
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up blob URL
        window.URL.revokeObjectURL(blobUrl);
    }

    /**
     * View a file in a new browser tab using authenticated request + blob URL
     * This preserves Bearer auth unlike directly navigating to the download URL
     * @param attachmentId - The GUID of the attachment
     * @param contentType - Optional MIME type for the blob
     */
    public async viewFileInTab(attachmentId: string, contentType?: string): Promise<void> {
        const url = this.getDownloadUrl(attachmentId);
        
        // Fetch with authentication (Http service handles Bearer token)
        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${await this.getBearerToken()}`
            }
        });
        
        if (!response.ok) {
            throw new Error(`Failed to load file: ${response.statusText}`);
        }
        
        const blob = await response.blob();
        // Use provided content type or fallback to blob's type
        const viewBlob = contentType ? new Blob([blob], { type: contentType }) : blob;
        const blobUrl = window.URL.createObjectURL(viewBlob);
        
        // Open in new tab
        const newTab = window.open(blobUrl, '_blank');
        
        if (!newTab) {
            window.URL.revokeObjectURL(blobUrl);
            throw new Error('Popup blocked - unable to open file in new tab');
        }
        
        // Clean up blob URL after a delay (gives browser time to load)
        setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
        }, 60000); // 1 minute cleanup delay
    }

    /**
     * Get the appropriate URL for a file - always returns download URL
     * Use viewFileInTab() for opening web-viewable files to preserve auth
     * @param attachmentId - The GUID of the attachment
     * @returns URL for downloading
     */
    public getFileUrl(attachmentId: string): string {
        return this.getDownloadUrl(attachmentId);
    }

    /**
     * Helper to get bearer token from Cache
     */
    private async getBearerToken(): Promise<string> {
        // Dynamically import to avoid circular dependency issues
        const { default: Cache } = await import('@/services/Cache');
        return (await Cache.get('bearer')) || '';
    }
}

const singleton = new AttachmentService();
export default singleton;
